/**
 * Admin → one site (container): its nodes (live: every link change, health
 * report and role change refetches them), "Add server" with the generated
 * `docker run` line, make the backup main, replace a node (confirmed by typing
 * its label; the replacement's install line follows), and the site's
 * emergency account (the password shown once).
 */
import type { EnrolmentCode } from '@acp/api-contract/sites.js';
import { useState } from 'react';
import { toast } from 'sonner';
import { useAdminSites, useEmergencyAccount, useEmergencyAccountWrite, useEnrolmentCode, useNow, useReplaceNode, useSiteNodes, useSwapRoles } from '../../app/hooks.js';
import { AddServerDialog } from '../../components/admin/AddServerDialog.js';
import { ConfirmDialog } from '../../components/admin/ConfirmDialog.js';
import { EmergencyAccountCard } from '../../components/admin/EmergencyAccountCard.js';
import { NodesTable } from '../../components/admin/NodesTable.js';
import { Button } from '../../components/ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card.js';
import { copyText, errorText, messageOf } from './common.js';

/** A replacement's code lives the longest D21 allows: the new host may take a while to set up. */
const REPLACEMENT_CODE_MINUTES = 24 * 60;

export function SiteAdminView({ siteId }: { readonly siteId: string }) {
  const sites = useAdminSites();
  const nodes = useSiteNodes(siteId);
  const emergency = useEmergencyAccount(siteId);
  const writeEmergency = useEmergencyAccountWrite();
  const code = useEnrolmentCode();
  const swap = useSwapRoles();
  const replace = useReplaceNode();
  const nowMs = useNow(30_000);
  const [adding, setAdding] = useState(false);
  const [enrolment, setEnrolment] = useState<EnrolmentCode | null>(null);
  const [replacing, setReplacing] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<string | null>(null);
  const [rotating, setRotating] = useState(false);

  const site = sites.data?.find((s) => s.siteId === siteId);
  const onprem = site?.kind !== 'cloud';
  const replacingNode = nodes.data?.find((n) => n.nodeId === replacing);

  const writePassword = (rotate: boolean) =>
    writeEmergency.mutate(
      { siteId, rotate },
      {
        onSuccess: (result) => {
          setRotating(false);
          setRevealed(result.password);
        },
      },
    );

  return (
    <div className="grid gap-4" data-testid="site-page">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <div className="grid gap-1.5">
            <CardTitle>{site?.name ?? siteId}</CardTitle>
            <CardDescription>
              {site?.kind === 'cloud' ? 'The cloud site: its nodes are your cloud productions’ tasks, started and stopped with them.' : `Servers of this site. Studios here: ${site?.studios.join(', ') || 'none yet'}.`}
            </CardDescription>
          </div>
          {onprem ? (
            <Button
              onClick={() => {
                code.reset();
                setEnrolment(null);
                setAdding(true);
              }}
              data-testid="add-server"
            >
              Add server
            </Button>
          ) : null}
        </CardHeader>
        <CardContent>
          <NodesTable
            nodes={nodes.data ?? []}
            nowMs={nowMs}
            loading={nodes.isFetching}
            error={errorText(nodes.error)}
            pending={swap.isPending || replace.isPending}
            onRetry={() => {
              void nodes.refetch();
            }}
            onMakeMain={(nodeId) =>
              swap.mutate({ siteId, mainNodeId: nodeId }, { onSuccess: () => toast.success('Roles swapped: the backup is main now'), onError: (err) => toast.error(messageOf(err)) })
            }
            onReplace={(nodeId) => {
              replace.reset();
              setReplacing(nodeId);
            }}
          />
        </CardContent>
      </Card>
      {onprem ? (
        <EmergencyAccountCard
          status={emergency.data ?? null}
          revealed={revealed}
          pending={writeEmergency.isPending}
          error={rotating ? null : errorText(writeEmergency.error)}
          onCreate={() => writePassword(false)}
          onRotate={() => {
            writeEmergency.reset();
            setRotating(true);
          }}
          onCopy={(text) => copyText(text, 'Password')}
          onDismiss={() => setRevealed(null)}
        />
      ) : null}
      <AddServerDialog
        open={adding}
        siteName={site?.name ?? siteId}
        enrolment={enrolment}
        pending={code.isPending}
        error={errorText(code.error)}
        onGenerate={(request) => code.mutate({ siteId, ...request }, { onSuccess: setEnrolment })}
        onCopy={(text) => copyText(text, 'Install command')}
        onOpenChange={(open) => {
          setAdding(open);
          if (!open) setEnrolment(null);
        }}
      />
      <ConfirmDialog
        open={replacing !== null}
        title={`Replace ${replacingNode?.label ?? 'node'}?`}
        description="Its key is revoked for good — it is refused from now on, its link is closed and its DNS name withdrawn — and you get the install command for its successor, which takes over its role."
        confirmLabel="Revoke and replace"
        typeToConfirm={replacingNode?.label ?? null}
        pending={replace.isPending}
        error={errorText(replace.error)}
        onConfirm={() => {
          if (replacing === null) return;
          replace.mutate(
            { siteId, nodeId: replacing, minutes: REPLACEMENT_CODE_MINUTES },
            {
              onSuccess: (successor) => {
                setReplacing(null);
                setEnrolment(successor);
                setAdding(true);
              },
            },
          );
        }}
        onOpenChange={(open) => (open ? undefined : setReplacing(null))}
      />
      <ConfirmDialog
        open={rotating}
        title="Rotate the emergency password?"
        description="The old password stops working, and sessions opened with it end at the servers’ next sync. The new one is shown once."
        confirmLabel="Rotate password"
        typeToConfirm={null}
        pending={writeEmergency.isPending}
        error={errorText(writeEmergency.error)}
        onConfirm={() => writePassword(true)}
        onOpenChange={setRotating}
      />
    </div>
  );
}
